import { AsyncHelper } from '@bryntum/schedulerpro';
import { debounce, Dictionary } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ResourceManagmentApi } from 'api/resource-management';
import { toastService } from 'components/molecules/toast';
import {
  ProjectsWithMembers,
  SchedulerReference,
} from 'components/organisms/resource-management/types';
import { FieldFilter } from 'components/organisms/tasks/filters';
import { GroupedFilter } from 'components/organisms/tasks/grouped-filter-popover';
import { useTranslation } from 'hooks/use-translation';
import { logger } from 'services';
import { ApiGroupFilter } from 'utils/simple-search-filter/simple-search-filter';
import { SchedulerResourceType } from '../resource-types';
import { useLoadingRenderer } from '../scheduler-config/loading-renderer';
import { useNoDataRender } from '../scheduler-config/no-data-renderer';
import { getProjectsSorter } from '../sorters';
import { isMatchingResource } from '../utils/resource-matching';

type FilteredResources = Record<number, string[]>;

interface Props {
  canApplyFilter: boolean;
  searchText: string;
  filter: GroupedFilter;
  schedulerRef: SchedulerReference;
  projectLookup: Dictionary<ProjectsWithMembers>;
  onFilterReset: () => void;
}
export const useProjectsViewFilter = ({
  canApplyFilter,
  searchText,
  filter,
  schedulerRef,
  projectLookup,
  onFilterReset,
}: Props): void => {
  const { showLoading, hideLoading } = useLoadingRenderer({ schedulerRef });

  const errorTitle = useTranslation('failure');
  const errorMessage = useTranslation('error');

  const [noData, setNoData] = useState(false);
  const [previousFilter, setPreviousFilter] =
    useState<GroupedFilter | undefined>(undefined);
  const [filteredProjectResources, setFilteredProjectResources] =
    useState<FilteredResources | undefined>(undefined);

  useNoDataRender({
    schedulerRef,
    noData,
    onReset: onFilterReset,
  });

  const filterApiHandler = useCallback(async () => {
    try {
      showLoading();
      await AsyncHelper.sleep(10);

      const { projectTabEntities } =
        await ResourceManagmentApi.getFilteredResourcesInProjects(filter);

      const projectResources = {} as FilteredResources;

      projectTabEntities.forEach((resource) => {
        // setting empty object for now
        // may have to extend in future if complex filters are introduced
        projectResources[resource.projectId] = [];
      });

      hideLoading();
      setFilteredProjectResources(projectResources);
    } catch (e) {
      toastService({
        title: errorTitle,
        message: errorMessage,
      }).error();

      hideLoading();
      setFilteredProjectResources(undefined);
    }
  }, [errorMessage, errorTitle, filter, hideLoading, showLoading]);

  const filterApiRef = useRef(filterApiHandler);

  useEffect(() => {
    filterApiRef.current = filterApiHandler;
  }, [filterApiHandler]);

  const debouncedFilterApi = useMemo(
    () =>
      debounce(() => {
        filterApiRef.current();
      }, 200),
    [],
  );

  const searchFunction = useMemo(
    () => isMatchingResource(searchText),
    [searchText],
  );

  useEffect(() => {
    const isFilterValid =
      filter.groupCriteria.filter((x) => FieldFilter.isValid(x.criteria))
        .length === filter.groupCriteria.length;

    const oldFilterQuery =
      previousFilter != null
        ? ApiGroupFilter.toQueryStringFormatForGroups(
            previousFilter.groupCriteria,
          )
        : undefined;
    const newFilterQuery = ApiGroupFilter.toQueryStringFormatForGroups(
      filter.groupCriteria,
    );

    if (
      isFilterValid &&
      (previousFilter == null ||
        newFilterQuery !== oldFilterQuery ||
        previousFilter.type !== filter.type)
    ) {
      setPreviousFilter(filter);
      if (newFilterQuery === '') {
        setFilteredProjectResources(undefined);
      } else {
        debouncedFilterApi();
      }
    }
  }, [debouncedFilterApi, filter, previousFilter]);

  const applyFilter = useCallback(() => {
    try {
      schedulerRef.current.instance.resourceStore.filter({
        filters: {
          id: 'projects-view-filter',
          filterBy: (resource) => {
            const type = resource.get('type');
            const searchResult =
              type === SchedulerResourceType.AssignUserOrPlaceholder
                ? searchFunction(resource.parent) ||
                  !!(
                    resource.parent.unfilteredChildren ??
                    resource.parent.children
                  ).find(
                    (child) =>
                      child.type === SchedulerResourceType.Role &&
                      child.children.find((grandChild) =>
                        searchFunction(grandChild),
                      ),
                  )
                : [
                    SchedulerResourceType.User,
                    SchedulerResourceType.Team,
                  ].includes(type)
                ? searchFunction(resource) ||
                  searchFunction(resource.parent.parent)
                : searchFunction(resource);

            let filterResult = false;

            switch (type) {
              case SchedulerResourceType.Project:
                filterResult =
                  filteredProjectResources == null ||
                  filteredProjectResources[resource.get('projectId')] != null;
                break;
              case SchedulerResourceType.Role:
              case SchedulerResourceType.AssignUserOrPlaceholder:
                filterResult =
                  filteredProjectResources == null ||
                  filteredProjectResources[resource.parent.get('projectId')] !=
                    null;
                break;
              case SchedulerResourceType.User:
              case SchedulerResourceType.Team:
                filterResult =
                  filteredProjectResources == null ||
                  filteredProjectResources[
                    resource.parent.parent.get('projectId')
                  ] != null;
                break;

              default:
                return filterResult;
            }

            return searchResult && filterResult;
          },
        },
        reapplyFilterOnAdd: true,
        reapplyFilterOnUpdate: true,
      });
      schedulerRef.current.instance.resourceStore.sort(
        getProjectsSorter(projectLookup),
      );
      setNoData(schedulerRef.current?.instance.resourceStore.allCount === 0);
    } catch (e) {
      logger.log('Error when filtering in people view', e);
    }
  }, [filteredProjectResources, projectLookup, schedulerRef, searchFunction]);

  const filterRef = useRef(applyFilter);

  useEffect(() => {
    filterRef.current = applyFilter;
  }, [applyFilter]);

  const debouncedFilter = useMemo(
    () =>
      debounce(() => {
        filterRef.current();
      }, 200),
    [],
  );

  useEffect(() => {
    if (!canApplyFilter) {
      return;
    }

    debouncedFilter();
  }, [canApplyFilter, debouncedFilter, applyFilter]);
};
